<template>
  <div class="data-edit data-edit--gradient">
    <form-factory
      formclass="data-edit__content"
      :record="currentPerson"
      :schema="fieldData"
      :layout="fieldLayout"
      :submitText="getUiString(['claim_details', 'details', 'passengers', 'save_button'])"
      :cancelText="getUiString(['claim_details', 'details', 'passengers', 'cancel_Button'])"
      :hasError="error"
      :isPassengerRequired="currentClaim.IsPassengerAddressRequired"
      @blurfield="checkAddress"
      @submit="checkSaveSettings"
      @reset="$emit('end-edit-person')"
      @copy-address="copyAddress"
      @fieldinput="handleFieldInput"
    >
      <h3 class="title--m">Passagier - {{ currentPerson.FullName || 'Unknown'}}</h3>
        <div v-if="!canModify" class="text">
          {{ getUiString(['claim_details', 'details', 'passengers', 'no_edit_legal']) }}
        </div>
      <template v-slot:bottomtext v-if="emailProtected">
        <div class="form__row">
          <div class="text">
            {{ getUiString(['claim_details', 'details', 'passengers', 'email_notification']) }}
          </div>
        </div>
      </template>
      <template v-slot:extra-content v-if="isMinor(currentPerson.BirthDate)" class="form__row">
        <claim-legal-representative
          :id="'LegalRepresentativeLink1'"
          :items="getAvailableLegalPersons1"
          :value="currentPerson.LegalRepresentativeLink1"
          :canModify= canModify
          :canComplete= canComplete
          :label="getUiString(['form_fields', 'LegalRepresentativeLink1'])"
          :IsBirthPlaceRequired="currentPerson.IsBirthPlaceRequired"
          @input="updateLegalRepresentative('LegalRepresentativeLink1', $event)" />

        <claim-legal-representative
          :id="'LegalRepresentativeLink2'"
          :items="getAvailableLegalPersons2"
          :value="currentPerson.LegalRepresentativeLink2"
          :canModify= canModify
          :canComplete= canComplete
          :IsBirthPlaceRequired="currentPerson.IsBirthPlaceRequired"
          :label="getUiString(['form_fields', 'LegalRepresentativeLink2'])"
          @input="updateLegalRepresentative('LegalRepresentativeLink2', $event)" />
      </template>
    </form-factory>
    <lennoc-modal v-if="hasProblem">
      <h2 class="title--m">{{ problem.title }}</h2>
      <p>{{ problem.text }}</p>
      <base-button-group :buttons="problem.buttons" @button-click="problemClickHandler"></base-button-group>
    </lennoc-modal>
    <lennoc-spinner v-show="busy" isFullscreen="false" />
  </div>
</template>

<script>
import day from 'dayjs'
import { mapGetters } from 'vuex'
import { getFieldKeys } from '@/utils/formComposition.js'
import { getFormFields } from '@/utils/formFields'

export default {
  name: 'EditPerson',
  props: {
    person: {
      type: Object,
      required: true
    },
    documentId: {
      type: String,
      default () {
        return ''
      }
    }
  },
  data: function () {
    return {
      currentPerson: null,
      busy: false,
      error: false,
      chkPc: '',
      chkHn: '',
      hasAddressError: false,
      emailProtected: false,
      fieldLayout: [],
      problem: {
        title: '',
        text: '',
        buttons: []
      }
    }
  },
  methods: {
    isMinor (date) {
      return (
        !!date &&
        day(date)
          .add(18, 'year')
          .isAfter(day())
      )
    },
    passengerType () {
      return !this.currentPerson ? 'nonPassenger'
        : this.isMinor(this.currentPerson.BirthDate)
          ? 'minorPassenger'
          : this.currentClaim.IsPassengerAddressRequired ? 'passengerAddressRequiredFields'
            : 'soloPassenger'
    },
    handleFieldInput ({ field, value }) {
      if (field === 'BirthDate') {
        this.currentPerson.BirthDate = value
        this.updateFieldLayout()
      }
    },

    updateFieldLayout () {
      const type = this.passengerType()
      this.fieldLayout = getFieldKeys(type)

      if (!this.currentPerson.IsBirthPlaceRequired) {
        const birthPlaceKey = 'BirthPlace'
        if (Array.isArray(this.fieldLayout[0])) {
          this.fieldLayout = this.fieldLayout.map(group =>
            Array.isArray(group) ? group.filter(field => field.trim() !== birthPlaceKey) : group
          )
        } else {
          this.fieldLayout = this.fieldLayout.filter(field => field.trim() !== birthPlaceKey)
        }
      }
    },
    updateLegalRepresentative (field, value) {
      this.$set(this.currentPerson, field, value)
      if (
        field === 'LegalRepresentativeLink1' &&
    this.currentPerson.LegalRepresentativeLink2 &&
    this.currentPerson.LegalRepresentativeLink2.PersonId === value.PersonId
      ) {
        this.$set(this.currentPerson, 'LegalRepresentativeLink2', null)
      } else if (
        field === 'LegalRepresentativeLink2' &&
    this.currentPerson.LegalRepresentativeLink1 &&
    this.currentPerson.LegalRepresentativeLink1.PersonId === value.PersonId
      ) {
        this.$set(this.currentPerson, 'LegalRepresentativeLink1', null)
      }
    },
    getTouchedClass (member) {
      return this.account[member] !== '' ? 'form__group--touched' : ''
    },
    async checkAddress ({ field, value }) {
      if (!this.currentPerson) {
        return
      }
      this.$set(this.currentPerson, field, value)

      const person = this.personById(this.person.Id)
      if (person) {
        this.$set(person, field, value)
      }
      const { PostalCode, HouseNumber } = this.currentPerson
      if (PostalCode && HouseNumber) {
        if (PostalCode !== this.chkPc || HouseNumber !== this.chkHn) {
          this.chkPc = PostalCode
          this.chkHn = HouseNumber
          this.busy = true

          try {
            const result = await this.$store.dispatch('claim/findAddress', {
              postalCode: PostalCode,
              houseNumber: HouseNumber
            })

            if (result) {
              this.hasAddressError = false
              this.currentPerson = { ...this.currentPerson, StreetName: result.Street, City: result.Town }

              if (person) {
                this.$set(person, 'StreetName', result.Street)
                this.$set(person, 'City', result.Town)
              }
            } else {
              this.hasAddressError = true
              this.currentPerson = { ...this.currentPerson, StreetName: '', City: '' }

              if (person) {
                this.$set(person, 'StreetName', '')
                this.$set(person, 'City', '')
              }
            }
          } catch (error) {
            this.hasAddressError = true
            this.$set(this.currentPerson, 'StreetName', '')
            this.$set(this.currentPerson, 'City', '')
            if (person) {
              this.$set(person, 'StreetName', '')
              this.$set(person, 'City', '')
            }
          } finally {
            this.busy = false
          }
        }
      }
    },

    copyAddress () {
      const accountAddress = this.account
      if (accountAddress) {
        const updatedPerson = {
          ...this.currentPerson,
          City: accountAddress.City || '',
          CountryCode: accountAddress.CountryCode || '',
          HouseNumber: accountAddress.HouseNumber || '',
          HouseNumberExtension: accountAddress.HouseNumberExtension || '',
          PostalCode: accountAddress.PostalCode || '',
          StreetName: accountAddress.StreetName || ''
        }
        this.$nextTick(() => {
          this.currentPerson = updatedPerson
        })
      } else {
        console.warn('Account address data is unavailable.')
      }
    },

    problemClickHandler (buttonId) {
      switch (buttonId) {
        case 'but_yes':
          this.saveSettings()
          break
        case 'but_hardcopy':
          this.$store.dispatch('claim/requestPoaHardcopy', {
            claimId: this.claimId,
            value: true
          })
          this.saveSettings()
          break
        case 'but_no':
          this.currentPerson.EMailAddress = ''
          break
        case 'but_email':
          this.currentPerson.EMailAddress = ''
          break
      }
      this.problem.title = ''
      this.problem.text = ''
      this.problem.buttons = []
    },
    checkSaveSettings (record) {
      const updatedPerson = { ...record }
      updatedPerson.LegalRepresentativeLink1 = this.currentPerson.LegalRepresentativeLink1
      updatedPerson.LegalRepresentativeLink2 = this.currentPerson.LegalRepresentativeLink2

      this.currentPerson = { ...this.currentPerson, ...updatedPerson }
      const accountEmail = this.accountEmail.toLowerCase()
      const emailfield = this.fieldData.EMailAddress
      if (emailfield && !emailfield.disabled && accountEmail === this.currentPerson.EMailAddress.toLowerCase()) {
        const emails = this.getAllEmails
        if (
          emails.filter(a => {
            return a.EMailAddress === accountEmail
          }).length > 1
        ) {
          this.problem.title = this.getUiString(['claim_details', 'details', 'passenger_edit_message', 'mailaddressAlreadyUsedTitle2'])
          this.problem.text = this.getUiString(['claim_details', 'details', 'passenger_edit_message', 'mailaddressAlreadyUsedText2'])
          this.problem.buttons = [
            {
              id: 'but_email',
              text: this.getUiString(['claim_details', 'details', 'passenger_edit_message', 'mailaddressAlreadyUsedButtonOk'])
            },
            {
              id: 'but_hardcopy',
              text: this.getUiString(['claim_details', 'details', 'passenger_edit_message', 'confirmHardcopy'])
            }
          ]
        } else {
          this.problem.title = this.getUiString(['claim_details', 'details', 'passenger_edit_message', 'mailaddressAlreadyUsedTitle'])
          this.problem.text = this.getUiString(['claim_details', 'details', 'passenger_edit_message', 'mailaddressAlreadyUsedText'])
          this.problem.buttons = [
            {
              id: 'but_yes',
              text: this.getUiString(['claim_details', 'details', 'passenger_edit_message', 'mailaddressAlreadyUsedButtonYes'])
            },
            {
              id: '',
              text: this.getUiString(['claim_details', 'details', 'passenger_edit_message', 'mailaddressAlreadyUsedButtonNo'])
            }
          ]
        }
      } else {
        this.saveSettings()
      }
    },
    saveSettings () {
      this.busy = true

      const updatedPerson = { ...this.personById(this.person.Id), ...this.currentPerson }

      this.$store
        .dispatch('claim/updatePerson', updatedPerson)
        .then(
          () => {
            this.$store.dispatch('claim/refreshClaim').then(() => {
              this.$emit('end-edit-person', 'ok')
            })
          },
          err => {
            this.$emit('edit-person-error', err)
            this.busy = false
            this.error = true
          }
        )
        .finally(() => {
          this.busy = false
        })
    }
  },
  mounted () {
    this.updateFieldLayout()
  },
  beforeMount () {
    this.currentPerson = this.personById(this.person.Id) || {}
  },
  computed: {
    ...mapGetters({
      legalPersons: 'claim/getLegalPersons',
      personById: 'claim/getPersonById',
      getUiString: 'uistrings/getUiString',
      accountEmail: 'account/getAccountEmail',
      getAllEmails: 'claim/getClaimEmailAddresses',
      claimId: 'claim/claimId',
      currentClaim: 'claim/currentClaim',
      account: 'account/shadowAccount'
    }),

    canModify () {
      return this.currentPerson.CanModify
    },
    canComplete () {
      return this.currentPerson.canComplete
    },
    hasProblem () {
      return this.problem.text !== ''
    },
    getErrorClass () {
      return this.error ? 'form__group--error' : ''
    },

    getAvailableLegalPersons1 () {
      const excludeId = this.currentPerson.LegalRepresentativeLink2
        ? this.currentPerson.LegalRepresentativeLink2.PersonId
        : ''
      return this.legalPersons.filter(p => p.Id !== excludeId)
    },
    getAvailableLegalPersons2 () {
      const excludeId = this.currentPerson.LegalRepresentativeLink1
        ? this.currentPerson.LegalRepresentativeLink1.PersonId
        : ''
      return this.legalPersons.filter(p => p.Id !== excludeId)
    },
    fieldData () {
      const fields = getFormFields(this.fieldLayout)
      const requiredFields = getFieldKeys('requiredFields')
      const refPerson = this.personById(this.person.Id)
      Object.keys(fields).forEach(f => {
        const field = fields[f]
        field.required = requiredFields.indexOf(f) !== -1
        if (refPerson.CanModify) {
          field.disabled = false
        } else if (refPerson.CanComplete) {
          if (f === 'MiddleName') {
            field.disabled = !!refPerson.LastName
          } else {
            field.disabled = !!refPerson[f]
          }
        } else {
          field.disabled = true
        }
        if (this.currentClaim.IsPassengerAddressRequired && ['PostalCode', 'HouseNumber', 'StreetName', 'City', 'CountryCode', 'HouseNumberExtension'].includes(f)) {
          field.disabled = false
        }
        if (f === 'EMailAddress') {
          const accountEmail = this.accountEmail
          if (!!accountEmail && refPerson.EMailAddress === accountEmail) {
            field.disabled = true
            this.emailProtected = true
          }
        }
        if (f === 'LegalRepresentativeLink1') {
          field.items = this.getAvailableLegalPersons1
        }
        if (f === 'LegalRepresentativeLink2') {
          field.items = this.getAvailableLegalPersons2
        }
      })
      return fields
    }
  }
}
</script>
